import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Sparkles,
  FileText,
  Upload,
  Loader2,
  CheckCircle2,
  Eye,
  ArrowRight,
  ArrowLeft,
  GraduationCap,
  Zap,
  ShieldCheck,
  BookOpen,
  XCircle,
  Play,
  Users,
  Clock,
  Star,
  Download,
  Award,
  Brain,
  Target,
} from "lucide-react";

const DEMO_TOPIC = "Design and Implementation of an Online Business Registration System";

const MOCK_TOC = [
  { chapter: "Chapter 1", title: "Introduction", pages: "1–8" },
  { chapter: "Chapter 2", title: "Literature Review", pages: "9–22" },
  { chapter: "Chapter 3", title: "Methodology", pages: "23–30" },
  { chapter: "Chapter 4", title: "Results and Discussion", pages: "31–42" },
  { chapter: "Chapter 5", title: "Summary & Recommendations", pages: "43–48" },
];

const VALUE_BADGES = [
  { icon: ShieldCheck, label: "AI-detection free" },
  { icon: Zap, label: "No editing required" },
  { icon: FileText, label: "Word & PDF supported" },
  { icon: BookOpen, label: "Ready-to-submit" },
];

const GENERATION_STAGES = [
  "Analyzing topic and structuring outline...",
  "Generating Chapter 1: Introduction...",
  "Writing Chapter 2: Literature Review...",
  "Composing Chapter 3: Methodology...",
  "Building Chapter 4: Results & Discussion...",
  "Finalizing Chapter 5: Conclusion...",
  "Compiling references and citations...",
  "Formatting document...",
];

const LIVE_STATS = [
  { value: "48", suffix: " pages", label: "Generated" },
  { value: "127", suffix: "+", label: "References" },
  { value: "0%", suffix: "", label: "AI Detected" },
];

export default function InteractiveDemo() {
  const [currentStep, setCurrentStep] = useState(0);
  const [demoTopic, setDemoTopic] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genStageIndex, setGenStageIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genCancelled, setGenCancelled] = useState(false);
  const [previewTab, setPreviewTab] = useState<"word" | "pdf">("word");
  const [activeTocItem, setActiveTocItem] = useState(0);

  // Auto-type effect for step 0
  useEffect(() => {
    if (currentStep === 0 && !isTyping && demoTopic === "") {
      const timeout = setTimeout(() => {
        setIsTyping(true);
        let i = 0;
        const interval = setInterval(() => {
          setDemoTopic(DEMO_TOPIC.slice(0, i + 1));
          i++;
          if (i >= DEMO_TOPIC.length) {
            clearInterval(interval);
            setIsTyping(false);
          }
        }, 35);
        return () => clearInterval(interval);
      }, 800);
      return () => clearTimeout(timeout);
    }
  }, [currentStep, isTyping, demoTopic]);

  // Generation simulation
  useEffect(() => {
    if (!isGenerating || genCancelled) return;
    const interval = setInterval(() => {
      setGenProgress((prev) => {
        const next = prev + 1.5;
        if (next >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          setCurrentStep(3);
          return 100;
        }
        return next;
      });
    }, 60);
    return () => clearInterval(interval);
  }, [isGenerating, genCancelled]);

  useEffect(() => {
    const stageIndex = Math.min(
      Math.floor((genProgress / 100) * GENERATION_STAGES.length),
      GENERATION_STAGES.length - 1
    );
    setGenStageIndex(stageIndex);
  }, [genProgress]);

  // Animate TOC highlight in preview
  useEffect(() => {
    if (currentStep !== 3) return;
    const interval = setInterval(() => {
      setActiveTocItem((prev) => (prev + 1) % MOCK_TOC.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [currentStep]);

  const startGeneration = useCallback(() => {
    setGenProgress(0);
    setGenStageIndex(0);
    setIsGenerating(true);
    setGenCancelled(false);
    setCurrentStep(2);
  }, []);

  const cancelGeneration = useCallback(() => {
    setIsGenerating(false);
    setGenCancelled(true);
    setGenProgress(0);
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      if (step === 2) {
        startGeneration();
      } else {
        setCurrentStep(step);
        if (step < 2) {
          setGenProgress(0);
          setGenCancelled(false);
          setIsGenerating(false);
        }
      }
    },
    [startGeneration]
  );

  const steps = [
    { label: "Enter Topic", icon: GraduationCap },
    { label: "Requirements", icon: Upload },
    { label: "AI Generates", icon: Sparkles },
    { label: "Preview", icon: Eye },
    { label: "Get Started", icon: ArrowRight },
  ];

  return (
    <section id="demo" className="py-16 sm:py-24 relative overflow-hidden">
      {/* Layered background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/[0.03] to-background pointer-events-none" />
      <div className="absolute top-20 left-1/4 w-72 h-72 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-accent/8 rounded-full blur-[140px] pointer-events-none" />

      {/* Floating decorative elements */}
      <div className="absolute top-32 right-[10%] w-2 h-2 rounded-full bg-primary/40 animate-float hidden lg:block" />
      <div className="absolute top-48 left-[8%] w-1.5 h-1.5 rounded-full bg-primary/30 animate-float-delayed hidden lg:block" />
      <div className="absolute bottom-40 right-[15%] w-3 h-3 rounded-full bg-primary/20 animate-float hidden lg:block" />

      <div className="container mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14 max-w-2xl mx-auto">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/10 px-4 py-1.5">
            <Play className="w-3 h-3 mr-1.5" />
            Interactive Demo
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            See How It <span className="gradient-text">Works</span>
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Watch EduGenix transform a topic into a complete, submission-ready project — in seconds.
          </p>
        </div>

        {/* Step Indicators — connected with line */}
        <div className="relative flex items-center justify-center gap-0 mb-10 sm:mb-14 max-w-2xl mx-auto px-4">
          {/* Connecting line */}
          <div className="absolute top-1/2 left-8 right-8 h-px bg-border/50 -translate-y-1/2 hidden sm:block" />
          <div
            className="absolute top-1/2 left-8 h-px bg-primary/50 -translate-y-1/2 transition-all duration-500 hidden sm:block"
            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%`, maxWidth: "calc(100% - 4rem)" }}
          />

          {steps.map((step, i) => {
            const Icon = step.icon;
            const isActive = currentStep === i;
            const isCompleted = currentStep > i;

            return (
              <button
                key={i}
                onClick={() => {
                  if (i <= currentStep || isCompleted) goToStep(i);
                }}
                className="relative z-10 flex flex-col items-center gap-1.5 px-2 sm:px-4 group"
              >
                <div
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-lg shadow-primary/40 scale-110 ring-4 ring-primary/20"
                      : isCompleted
                      ? "bg-primary/20 text-primary ring-2 ring-primary/30"
                      : "bg-muted/60 text-muted-foreground group-hover:bg-muted"
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4 sm:w-5 sm:h-5" />}
                </div>
                <span
                  className={`text-[10px] sm:text-xs font-medium transition-colors whitespace-nowrap ${
                    isActive ? "text-primary" : isCompleted ? "text-primary/70" : "text-muted-foreground"
                  }`}
                >
                  {step.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Demo Content Area */}
        <div className="max-w-4xl mx-auto">
          {/* STEP 0: Enter Topic */}
          {currentStep === 0 && (
            <div className="animate-fade-in space-y-6">
              {/* Main card */}
              <div className="rounded-2xl border border-border/40 bg-card/90 backdrop-blur-sm shadow-2xl overflow-hidden">
                {/* Header band */}
                <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-6 py-4 border-b border-border/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-heading font-bold text-foreground">Enter Your Project Topic</h3>
                      <p className="text-xs text-muted-foreground">Just type or paste — EduGenix handles everything else</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-8 space-y-5">
                  {/* Topic input with glow */}
                  <div className="relative group">
                    <div className="absolute -inset-1 bg-primary/10 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative w-full min-h-[90px] rounded-xl border-2 border-primary/30 bg-background/80 p-4 text-foreground text-sm sm:text-base leading-relaxed transition-all hover:border-primary/50">
                      {demoTopic}
                      {isTyping && <span className="inline-block w-0.5 h-5 bg-primary animate-pulse ml-0.5 align-middle" />}
                      {!isTyping && demoTopic.length > 0 && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px]">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Ready
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Feature cards row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {VALUE_BADGES.map(({ icon: Icon, label }, i) => (
                      <div
                        key={label}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-muted/40 border border-border/30 text-xs text-foreground hover:bg-muted/60 hover:border-primary/20 transition-all group cursor-default"
                        style={{ animationDelay: `${i * 0.1}s` }}
                      >
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                          <Icon className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <span className="font-medium leading-tight">{label}</span>
                      </div>
                    ))}
                  </div>

                  <Button size="lg" className="w-full h-12 sm:h-14 text-base font-semibold gap-2" onClick={() => goToStep(1)} disabled={isTyping}>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Side stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Clock, val: "< 30s", sub: "Generation time" },
                  { icon: Star, val: "4.9/5", sub: "Student rating" },
                  { icon: Download, val: "120K+", sub: "Projects delivered" },
                ].map(({ icon: Icon, val, sub }) => (
                  <div key={sub} className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-muted/30 border border-border/20">
                    <Icon className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-foreground">{val}</p>
                      <p className="text-[10px] text-muted-foreground">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 1: Upload Requirements */}
          {currentStep === 1 && (
            <div className="animate-fade-in space-y-6">
              <div className="rounded-2xl border border-border/40 bg-card/90 backdrop-blur-sm shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-accent/10 via-accent/5 to-transparent px-6 py-4 border-b border-border/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-heading font-bold text-foreground">
                        Add Requirements
                        <Badge variant="outline" className="ml-2 text-[10px] align-middle">Optional</Badge>
                      </h3>
                      <p className="text-xs text-muted-foreground">Upload your school's guidelines for a tailored result</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-8 space-y-5">
                  {/* Upload zone */}
                  <div className="relative group cursor-pointer">
                    <div className="absolute -inset-1 bg-primary/5 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative border-2 border-dashed border-border/50 rounded-xl p-8 sm:p-10 text-center space-y-4 hover:border-primary/40 transition-all bg-muted/10 group-hover:bg-muted/20">
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Upload className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Upload PDF, DOCX, or Image</p>
                        <p className="text-xs text-muted-foreground mt-1">Max 10MB • Your file stays private & encrypted</p>
                      </div>
                      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> .pdf</span>
                        <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> .docx</span>
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> .jpg/.png</span>
                      </div>
                    </div>
                  </div>

                  {/* Pro tip */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/15">
                    <Brain className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">Pro tip</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Uploading guidelines ensures the AI matches your exact structure, formatting, and citation style.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" size="lg" className="flex-1 h-12" onClick={() => goToStep(0)}>
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                    <Button size="lg" className="flex-[2] h-12 font-semibold gap-2" onClick={() => goToStep(2)}>
                      <Sparkles className="w-4 h-4" />
                      Generate Project
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: AI Generating */}
          {currentStep === 2 && (
            <div className="animate-fade-in space-y-6">
              <div className="rounded-2xl border-2 border-primary/30 bg-card/90 backdrop-blur-sm shadow-2xl overflow-hidden relative">
                {/* Animated top border glow */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent animate-pulse-glow" />

                <div className="p-5 sm:p-8 space-y-6">
                  <div className="text-center space-y-3">
                    {/* Animated AI orb */}
                    <div className="relative w-20 h-20 mx-auto mb-2">
                      <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" style={{ animationDuration: "2s" }} />
                      <div className="absolute inset-2 rounded-full bg-primary/10 animate-pulse" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Sparkles className="w-9 h-9 text-primary animate-spin" style={{ animationDuration: "3s" }} />
                      </div>
                      <div className="absolute -inset-2 rounded-full border-2 border-dashed border-primary/20 animate-spin" style={{ animationDuration: "10s" }} />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                      {genCancelled ? "Generation Cancelled" : "Generating Your Project…"}
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      {genCancelled ? "No credits consumed." : GENERATION_STAGES[genStageIndex]}
                    </p>
                  </div>

                  {!genCancelled && (
                    <>
                      {/* Progress bar with glow */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Progress</span>
                          <span className="font-mono font-bold text-primary text-sm">{Math.round(genProgress)}%</span>
                        </div>
                        <div className="relative">
                          <Progress value={genProgress} className="h-3" />
                          <div
                            className="absolute top-0 left-0 h-3 rounded-full bg-primary/30 blur-sm transition-all"
                            style={{ width: `${genProgress}%` }}
                          />
                        </div>
                      </div>

                      {/* Chapter progress cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {MOCK_TOC.map((item, i) => {
                          const done = genProgress > ((i + 1) / MOCK_TOC.length) * 85;
                          const active = !done && genProgress > (i / MOCK_TOC.length) * 85;
                          return (
                            <div
                              key={i}
                              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                                done
                                  ? "bg-primary/10 border border-primary/20 text-primary"
                                  : active
                                  ? "bg-primary/5 border border-primary/10 text-foreground"
                                  : "bg-muted/20 border border-border/20 text-muted-foreground"
                              }`}
                            >
                              {done ? (
                                <CheckCircle2 className="w-4 h-4 shrink-0" />
                              ) : active ? (
                                <Loader2 className="w-4 h-4 shrink-0 animate-spin text-primary" />
                              ) : (
                                <div className="w-4 h-4 shrink-0 rounded-full border border-border/40" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{item.chapter}: {item.title}</p>
                                <p className="text-[10px] opacity-60">{item.pages} pages</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Live stats during generation */}
                      <div className="grid grid-cols-3 gap-2">
                        {LIVE_STATS.map(({ value, suffix, label }) => (
                          <div key={label} className="text-center py-3 rounded-xl bg-muted/30 border border-border/20">
                            <p className="text-lg font-bold text-foreground tabular-nums">
                              {genProgress < 90 ? <Loader2 className="w-4 h-4 mx-auto animate-spin text-muted-foreground" /> : <>{value}<span className="text-primary">{suffix}</span></>}
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{label}</p>
                          </div>
                        ))}
                      </div>

                      <Button variant="destructive" size="lg" className="w-full h-12 font-semibold gap-2" onClick={cancelGeneration}>
                        <XCircle className="w-5 h-5" />
                        Cancel Generation
                      </Button>
                      <p className="text-center text-[11px] text-muted-foreground">Cancelling does not consume any credits.</p>
                    </>
                  )}

                  {genCancelled && (
                    <div className="text-center space-y-4">
                      <div className="p-4 rounded-xl bg-muted/50 border border-border/50 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 inline mr-1 text-primary" />
                        No downloads consumed. You can try again anytime.
                      </div>
                      <Button size="lg" className="w-full h-12 font-semibold gap-2" onClick={() => goToStep(2)}>
                        <Sparkles className="w-4 h-4" />
                        Try Again
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Preview Result */}
          {currentStep === 3 && (
            <div className="animate-fade-in space-y-6">
              <div className="rounded-2xl border border-border/40 bg-card/90 backdrop-blur-sm shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-6 py-4 border-b border-border/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center animate-scale-in">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-heading font-bold text-foreground">Project Ready!</h3>
                        <p className="text-xs text-muted-foreground">Your submission-ready document is complete</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-primary/20 text-primary border-primary/30">
                        <Award className="w-3 h-3 mr-1" />
                        48 Pages
                      </Badge>
                      <Badge variant="outline" className="border-primary/20 text-primary/80 hidden sm:flex">
                        127+ Refs
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-8 space-y-5">
                  {/* Document preview mock */}
                  <div className="relative rounded-xl border border-border/30 overflow-hidden shadow-xl">
                    {/* Window chrome */}
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-secondary/80 border-b border-border/30">
                      <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                        <div className="w-2.5 h-2.5 rounded-full bg-warning/60" />
                        <div className="w-2.5 h-2.5 rounded-full bg-primary/60" />
                      </div>
                      <span className="text-[10px] text-muted-foreground ml-3 font-mono truncate">
                        Online_Business_Registration_System.docx
                      </span>
                    </div>

                    {/* Document body */}
                    <div className="grid grid-cols-12 min-h-[360px] sm:min-h-[420px] bg-secondary/40">
                      {/* TOC sidebar */}
                      <div className="col-span-4 sm:col-span-3 border-r border-border/20 p-3 sm:p-4 space-y-3">
                        <p className="text-[10px] font-semibold text-primary/70 uppercase tracking-wider">Contents</p>
                        <div className="space-y-1">
                          {MOCK_TOC.map((item, i) => (
                            <div
                              key={i}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[10px] sm:text-xs transition-all duration-500 cursor-default ${
                                activeTocItem === i
                                  ? "bg-primary/15 text-primary font-medium shadow-sm shadow-primary/10"
                                  : "text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              <div className={`w-1.5 h-1.5 rounded-full shrink-0 transition-all duration-500 ${activeTocItem === i ? "bg-primary scale-125" : "bg-muted-foreground/30"}`} />
                              <span className="truncate">{item.title}</span>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-border/20 pt-2 mt-2 space-y-1.5">
                          <div className="h-2 w-3/4 rounded-full bg-muted-foreground/10" />
                          <div className="h-2 w-1/2 rounded-full bg-muted-foreground/10" />
                          <div className="h-2 w-2/3 rounded-full bg-muted-foreground/10" />
                        </div>
                      </div>

                      {/* Main content area */}
                      <div className="col-span-8 sm:col-span-9 p-4 sm:p-6 space-y-4 relative">
                        {/* Title block */}
                        <div className="space-y-2">
                          <div className="h-4 w-4/5 rounded bg-primary/25 animate-pulse" style={{ animationDuration: '3s' }} />
                          <div className="h-2.5 w-2/3 rounded bg-muted-foreground/15" />
                        </div>

                        {/* Abstract block */}
                        <div className="p-3 rounded-lg bg-muted/30 border border-border/20 space-y-1.5">
                          <div className="h-2 w-16 rounded bg-primary/30" />
                          {[92, 97, 88, 75, 82].map((w, i) => (
                            <div key={i} className="h-1.5 rounded bg-muted-foreground/10" style={{ width: `${w}%` }} />
                          ))}
                        </div>

                        {/* Chapter heading */}
                        <div className="space-y-2 pt-1">
                          <div className="h-2.5 w-48 rounded bg-primary/20" />
                          {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-1.5 rounded bg-muted-foreground/8" style={{ width: `${78 + Math.sin(i * 1.8) * 18}%` }} />
                          ))}
                        </div>

                        {/* Inline figure/chart */}
                        <div className="flex items-center justify-center h-20 rounded-lg bg-muted/20 border border-border/15">
                          <div className="flex items-end gap-1.5 h-12">
                            {[40, 65, 35, 85, 55, 75, 45, 60].map((h, i) => (
                              <div
                                key={i}
                                className="w-3 rounded-t bg-primary/25 transition-all duration-700"
                                style={{ height: `${h}%`, animationDelay: `${i * 0.1}s` }}
                              />
                            ))}
                          </div>
                        </div>

                        {/* More body text */}
                        <div className="space-y-1.5">
                          <div className="h-2.5 w-36 rounded bg-primary/18" />
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-1.5 rounded bg-muted-foreground/8" style={{ width: `${65 + Math.cos(i) * 22}%` }} />
                          ))}
                        </div>

                        {/* References hint */}
                        <div className="space-y-1.5 pt-1">
                          <div className="h-2 w-20 rounded bg-primary/15" />
                          {[...Array(2)].map((_, i) => (
                            <div key={i} className="h-1 rounded bg-muted-foreground/6" style={{ width: `${70 + i * 10}%` }} />
                          ))}
                        </div>

                        {/* Blur overlay with sign-up nudge */}
                        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-secondary/95 via-secondary/70 to-transparent flex items-end justify-center pb-4 pointer-events-none">
                          <Badge className="bg-primary/20 text-primary border-primary/30 backdrop-blur-sm pointer-events-auto text-xs px-3 py-1.5 shadow-lg shadow-primary/10">
                            <Eye className="w-3 h-3 mr-1.5" />
                            Sign up to view full content
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Floating badges */}
                    <div className="absolute bottom-24 left-2 sm:left-4 animate-fade-up z-10" style={{ animationDelay: "0.3s" }}>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card/95 border border-primary/20 shadow-lg shadow-primary/10 backdrop-blur-sm">
                        <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
                          <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-foreground">AI Detection</p>
                          <p className="text-[10px] font-bold text-primary">0% Detected</p>
                        </div>
                      </div>
                    </div>

                    <div className="absolute top-14 right-2 sm:right-4 animate-fade-up z-10" style={{ animationDelay: "0.6s" }}>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card/95 border border-primary/20 shadow-lg shadow-primary/10 backdrop-blur-sm">
                        <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
                          <Target className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-foreground">Quality Score</p>
                          <p className="text-[10px] font-bold text-primary">98/100</p>
                        </div>
                      </div>
                    </div>

                    {/* Glow overlay */}
                    <div
                      className="absolute inset-0 pointer-events-none rounded-xl"
                      style={{ background: "radial-gradient(ellipse at 70% 20%, hsl(var(--primary) / 0.08) 0%, transparent 60%)" }}
                    />
                  </div>

                  {/* Format download buttons — prominent like reference */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPreviewTab("word")}
                      className={`group relative flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 transition-all duration-300 ${
                        previewTab === "word"
                          ? "border-primary/50 bg-primary/10 shadow-lg shadow-primary/15"
                          : "border-border/30 bg-muted/20 hover:border-primary/30 hover:bg-primary/5"
                      }`}
                    >
                      <FileText className={`w-8 h-8 transition-colors ${previewTab === "word" ? "text-primary" : "text-muted-foreground group-hover:text-primary/70"}`} />
                      <span className={`text-xs font-semibold ${previewTab === "word" ? "text-primary" : "text-muted-foreground"}`}>Word Document</span>
                    </button>
                    <button
                      onClick={() => setPreviewTab("pdf")}
                      className={`group relative flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 transition-all duration-300 ${
                        previewTab === "pdf"
                          ? "border-primary/50 bg-primary/10 shadow-lg shadow-primary/15"
                          : "border-border/30 bg-muted/20 hover:border-primary/30 hover:bg-primary/5"
                      }`}
                    >
                      <Sparkles className={`w-8 h-8 transition-colors ${previewTab === "pdf" ? "text-primary" : "text-muted-foreground group-hover:text-primary/70"}`} />
                      <span className={`text-xs font-semibold ${previewTab === "pdf" ? "text-primary" : "text-muted-foreground"}`}>PDF Export</span>
                    </button>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2">
                    {LIVE_STATS.map(({ value, suffix, label }) => (
                      <div key={label} className="text-center py-3 rounded-xl bg-muted/30 border border-border/20">
                        <p className="text-base font-bold text-foreground tabular-nums">{value}<span className="text-primary">{suffix}</span></p>
                        <p className="text-[10px] text-muted-foreground">{label}</p>
                      </div>
                    ))}
                  </div>

                  <Button size="lg" className="w-full h-12 sm:h-14 text-base font-semibold gap-2 shadow-lg shadow-primary/20" onClick={() => setCurrentStep(4)}>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: CTA */}
          {currentStep === 4 && (
            <div className="animate-fade-in space-y-6">
              <div className="relative rounded-2xl border-2 border-primary/30 overflow-hidden shadow-2xl">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-card via-card to-primary/10 pointer-events-none" />
                <div className="absolute top-0 right-0 w-60 h-60 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />

                <div className="relative p-6 sm:p-10 space-y-8 text-center">
                  {/* Icon */}
                  <div className="relative w-20 h-20 mx-auto">
                    <div className="absolute inset-0 rounded-full bg-primary/15 animate-pulse" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <GraduationCap className="w-10 h-10 text-primary" />
                    </div>
                    <div className="absolute -inset-3 rounded-full border border-primary/10" />
                  </div>

                  <div className="space-y-3 max-w-lg mx-auto">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                      Ready to Get Your <span className="gradient-text">Project?</span>
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      Create a free account to generate and download your full, submission-ready academic project.
                    </p>
                  </div>

                  {/* Value props grid */}
                  <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
                    {[
                      { icon: Download, text: "3 free downloads" },
                      { icon: FileText, text: "Word & PDF export" },
                      { icon: ShieldCheck, text: "AI-detection free" },
                      { icon: BookOpen, text: "All citation styles" },
                    ].map(({ icon: Icon, text }) => (
                      <div key={text} className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-muted/30 border border-border/20 text-sm text-left">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-foreground font-medium text-xs">{text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                    <Button size="xl" className="flex-1 font-bold gap-2 shadow-lg shadow-primary/25" asChild>
                      <Link to="/auth">
                        <Sparkles className="w-5 h-5" />
                        Get Started Free
                      </Link>
                    </Button>
                    <Button variant="heroOutline" size="xl" className="flex-1 font-semibold gap-2" asChild>
                      <Link to="/auth">Create Free Account</Link>
                    </Button>
                  </div>

                  {/* Social proof */}
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4 text-primary" />
                    Trusted by <span className="text-foreground font-semibold">10,000+</span> students worldwide
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom social proof */}
        <div className="flex items-center justify-center gap-4 sm:gap-8 mt-10 sm:mt-14 text-xs sm:text-sm text-muted-foreground flex-wrap">
          {[
            { icon: ShieldCheck, text: "No credit card required" },
            { icon: Zap, text: "Generated in under 30s" },
            { icon: Users, text: "10,000+ students served" },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-1.5">
              <Icon className="w-4 h-4 text-primary" />
              <span>{text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
